import { defineComponent as _defineComponent } from 'vue'
import { makeFormModel } from 'ah-common-lib/src/form/helpers';
import { FormDefinition, FormEvent } from 'ah-common-lib/src/form/interfaces';
import { radioField, singleInputDateField, sliderField } from 'ah-common-lib/src/form/models';
import { reactive, watch, computed } from 'vue';
import { getChildModel } from 'ah-common-lib/src/form/helpers';
import { useAhTradesState } from '../..';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import {
  AmountType,
  CreateVanillaOptionsPriceRequest,
  NonTradeableDays,
  VanillaPriceResponse,
  PricingEngineErrorCodes,
  PremiumPaymentType,
  FeatureFlag,
} from 'ah-api-gateways';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import { setState } from 'ah-common-lib/src/form/helpers';
import { DebouncedFunc, debounce, isEqual } from 'lodash';
import { date, disallowStateDates, ifTest, minDate } from 'ah-common-lib/src/form/validators';
import { required } from '@vuelidate/validators';
import { startOfTomorrow, addDays } from 'date-fns';
import { ApiError } from 'ah-requests';
import { useFeatureFlagStore } from 'ah-common-stores';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';


export default /*#__PURE__*/_defineComponent({
  __name: 'OptionSolutionDetailsForm',
  props: {
    solution: null,
    isFirst: { type: Boolean },
    isLast: { type: Boolean },
    index: null,
    apiError: null
  },
  emits: ["remove:solution", "sort:solutions", "update:price", "update:validation", "update:solution"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'remove:solution'): void;
  (e: 'sort:solutions', delta: number): void;
  (e: 'update:price', value: CreateVanillaOptionsPriceRequest): void;
  (e: 'update:validation', value: boolean): void;
  (e: 'update:solution', value: VanillaPriceResponse): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  solution: VanillaPriceResponse;
  isFirst: boolean;
  isLast: boolean;
  index: number;
  apiError?: null | ApiError;
};



const tradeState = useAhTradesState();



const state = reactive<{
  debouncedStrikeRate: DebouncedFunc<() => void> | null;
  holidays: string[];
  weekends: string[];
  apiError: ApiError | null;
}>({
  debouncedStrikeRate: null,
  holidays: [],
  weekends: [],
  apiError: null,
});

const onBehalfOfClient = useOnBehalfOf();

const displayMonthlyPaymentOption = computed(() =>
  useFeatureFlagStore().isFeatureFlagOn(FeatureFlag.VANILLA_OPTIONS_HNPL_ENABLED, onBehalfOfClient.value?.id)
);

const strikeRateForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'strikeRateForm',
    title: '',
    fieldType: 'form',
    fields: [
      sliderField('strikeRate', 'Strike Rate', {
        class: 'zigzag-labels with-spot-rate',
        min: 0.1,
        max: 1,
        marks: [],
        tooltipFormatter: (val: number) => tradeState.store.useSettingsStore().displayRate(val),
        valueToInputFn: (val: number) => tradeState.store.useSettingsStore().displayRate(val),
        interval: 0.0001,
        withMathFloor: false,
      }),
    ],
  }),
  validation: null,
});

const settlementDateForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'settlementDateForm',
    title: '',
    fieldType: 'form',
    fields: [
      singleInputDateField(
        'settlementDate',
        'Settlement Date',
        {
          required: true,
          errorMessages: {
            minDate: 'Settlement Date must be after two working days (T+2), please try again',
            required: 'Required field',
            holidays: 'Settlement date cannot be on a holiday, please try again',
            weekend: 'Settlement date cannot be on a weekend, please try again',
          },
        },
        {
          required: ifTest(required, (val) => !(val instanceof Date)),
          minDate: minDate(() => addDays(startOfTomorrow(), 1)),
          date: ifTest(date, (val) => val instanceof Date),
          holidays: disallowStateDates('settlementDate', 'holidays'),
          weekend: disallowStateDates('settlementDate', 'weekends'),
        }
      ),
    ],
  }),
  validation: null,
});

const options = computed(() => {
  if (displayMonthlyPaymentOption.value) {
    return [
      {
        label: 'Pay Now',
        value: PremiumPaymentType.UPFRONT,
      },
      {
        label: 'Pay Your Premium Monthly',
        value: PremiumPaymentType.MONTHLY,
      },
    ];
  }
  return [
    {
      label: 'Pay Now',
      value: PremiumPaymentType.UPFRONT,
    },
  ];
});

const premiumPaymentForm = reactive<FormDefinition>({
  form: makeFormModel({
    name: 'premiumPaymentForm',
    title: '',
    fieldType: 'form',
    fields: [
      radioField('paymentType', '', options.value, {
        defaultValue: props.solution.premiumType ? props.solution.premiumType : PremiumPaymentType.UPFRONT,
        inputClass: 'separator custom-control custom-radio col col-12 my-0',
        inline: '',
      }),
    ],
  }),
  validation: null,
});

function loadNonTradeableDays() {
  tradeState.services.pricingEngine
    .getNonTradeableDays(props.solution.hedgingProduct, props.solution.currencyPair)
    .subscribe((result: NonTradeableDays) => {
      state.holidays = result.holidays.filter((d) => !result.weekend.includes(d));
      state.weekends = result.weekend;

      if (state.holidays) {
        setState(getChildModel(settlementDateForm.form, 'settlementDate')!, 'holidays', state.holidays);
      }
      if (state.weekends) {
        setState(getChildModel(settlementDateForm.form, 'settlementDate')!, 'weekends', state.weekends);
      }
    });
}

watch(
  () => props.solution.currencyPair,
  () => {
    loadNonTradeableDays();
  },
  { immediate: true }
);

const sellCcy = computed(() => {
  if (props.solution.ccy1.amountType === AmountType.SELL) {
    return props.solution.ccy1;
  }

  return props.solution.ccy2;
});

const buyCcy = computed(() => {
  if (props.solution.ccy1.amountType === AmountType.BUY) {
    return props.solution.ccy1;
  }

  return props.solution.ccy2;
});

const sellAmount = computed(() => formatCurrencyValue(sellCcy.value.clientAmount));

const buyAmount = computed(() => formatCurrencyValue(buyCcy.value.clientAmount));

const strikeRate = computed(() => formatCurrencyValue(isPrimaryRate.value.optionsCurrencyData.selectedStrikeRate));

const sellCurrency = computed(() => sellCcy.value.currency);

const buyCurrency = computed(() => buyCcy.value.currency);

const showProtectionInSell = computed(() => props.solution.premiumCurrency === sellCcy.value.currency);

const isPrimaryRate = computed(() => {
  if (props.solution.ccy1.isPrimaryRate === true) {
    return props.solution.ccy1;
  }
  return props.solution.ccy2;
});

const isFixedSide = computed(() => {
  if (props.solution.ccy1.isFixedSide === true) {
    return props.solution.ccy1;
  }
  return props.solution.ccy2;
});

state.debouncedStrikeRate = debounce(() => {
  if (strikeRateForm.form.strikeRate && !state.apiError) {
    emit('update:price', {
      clientId: props.solution.clientId,
      sellCurrency: sellCurrency.value,
      buyCurrency: buyCurrency.value,
      amount: isFixedSide.value.clientAmount,
      amountType: isFixedSide.value.amountType,
      settlementDate: formatDate(new Date(props.solution.settlementDate), 'yyyy-MM-dd'),
      strikeRate: strikeRateForm.form.strikeRate,
    });
  }
}, 800);

function setErrorMessage(subError: ApiError) {
  setState(getChildModel(settlementDateForm.form, subError.field!)!, 'errors', [
    {
      name: 'peError',
      error:
        subError.code === PricingEngineErrorCodes.EXPIRY_DATE_INVALID
          ? 'Settlement date is too far in the future, please try again'
          : subError.message,
    },
  ]);

  state.apiError = subError;
}

watch(
  () => props.apiError,
  () => {
    if (props.apiError && props.apiError.field) {
      setErrorMessage(props.apiError);
    }
  }
);

const isInvalid = computed(() => settlementDateForm.validation?.$invalid || strikeRateForm.validation?.$invalid);

function updateValidations() {
  if (isInvalid.value) {
    emit('update:validation', isInvalid.value);
  }
}
watch(
  isInvalid,
  () => {
    updateValidations();
  },
  { immediate: true }
);

function onSettlementDateFormEvent(formEvent: FormEvent) {
  if (formEvent.event === 'form-field-set-value') {
    if (settlementDateForm.validation?.$invalid === false) {
      state.apiError = null;

      emit('update:price', {
        clientId: props.solution.clientId,
        sellCurrency: sellCcy.value.currency,
        buyCurrency: buyCcy.value.currency,
        amount: isFixedSide.value.clientAmount,
        amountType: isFixedSide.value.amountType,
        settlementDate: formatDate(new Date(settlementDateForm.form.settlementDate), 'yyyy-MM-dd'),
        strikeRate: strikeRateForm.form.strikeRate,
      });
    }
  }
}

function onFormEvent(formEvent: FormEvent) {
  if (formEvent.event === 'form-field-set-value') {
    state.debouncedStrikeRate!();
  }
}

function displayRate(value: number) {
  if (isPrimaryRate.value) {
    /*
        / if difference between both spot rate and forward rate is small and overlap,
        / hide the lowest (if CALL option type) or highest (if PUT option type) since the rate is displayed anyway on top/bottom.
      */
    let displayedRate;
    isPrimaryRate.value.optionsCurrencyData.optionType === 'PUT'
      ? (displayedRate = Math.max(
          isPrimaryRate.value.optionsCurrencyData.forwardRate,
          isPrimaryRate.value.optionsCurrencyData.spotRate
        ))
      : (displayedRate = Math.min(
          isPrimaryRate.value.optionsCurrencyData.forwardRate,
          isPrimaryRate.value.optionsCurrencyData.spotRate
        ));

    if (!equalToRate(value)) {
      return tradeState.store.useSettingsStore().displayRate(value);
    } else if (value === displayedRate) {
      return tradeState.store.useSettingsStore().displayRate(value);
    }
  }
}

function calcRatePosition(rate: string) {
  if (isPrimaryRate.value) {
    const rtl =
      isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates[0] <
      isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates[
        isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates.length - 1
      ];
    const max = Math.max(...isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates);
    const min = Math.min(...isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates);
    const value = ((isPrimaryRate.value.optionsCurrencyData as any)[rate] as number) || min;

    return {
      [rtl ? 'left' : 'right']: `${((value - min) / (max - min)) * 100}%`,
    };
  }

  return {};
}

function equalToRate(value: number) {
  return (
    value === isPrimaryRate.value.optionsCurrencyData!.spotRate ||
    value === isPrimaryRate.value.optionsCurrencyData!.forwardRate
  );
}

function updateStrikeRates() {
  if (strikeRateForm && isPrimaryRate.value && isPrimaryRate.value.optionsCurrencyData) {
    const strikeRateField = getChildModel(strikeRateForm.form, 'strikeRate')!;

    setState(strikeRateField, 'max', Math.max(...isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates));
    setState(strikeRateField, 'min', Math.min(...isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates));
    setState(
      strikeRateField,
      'maxValue',
      Math.max(
        isPrimaryRate.value.optionsCurrencyData.maxStrikeInStrikeRange,
        isPrimaryRate.value.optionsCurrencyData.minStrikeInStrikeRange
      )
    );
    setState(
      strikeRateField,
      'minValue',
      Math.min(
        isPrimaryRate.value.optionsCurrencyData.maxStrikeInStrikeRange,
        isPrimaryRate.value.optionsCurrencyData.minStrikeInStrikeRange
      )
    );
    setState(strikeRateField, 'marks', isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates);
    setState(strikeRateField, 'defaultValue', isPrimaryRate.value.optionsCurrencyData.selectedStrikeRate);

    const isLeftToRight =
      isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates[0] <
      isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates[
        isPrimaryRate.value.optionsCurrencyData.defaultStrikeRates.length - 1
      ];
    const isPut = isPrimaryRate.value.optionsCurrencyData.optionType === 'PUT';

    if (isPut === isLeftToRight) {
      setState(strikeRateField, 'process', (dotsPos: number[]) => [[dotsPos[0], 100]]);
    } else {
    }

    setState(strikeRateField, 'direction', isLeftToRight ? 'ltr' : 'rtl');

    strikeRateForm.form.strikeRate = isPrimaryRate.value.optionsCurrencyData.selectedStrikeRate;
  }
}

function updateSettlementDate() {
  if (props.solution.settlementDate) {
    settlementDateForm.form.settlementDate = new Date(props.solution.settlementDate);
  }
}

watch(
  () => props.solution.settlementDate,
  (newVal, oldVal) => {
    if (!isEqual(oldVal, newVal)) {
      updateSettlementDate();
    }
  },
  { immediate: true }
);

watch(
  () => isPrimaryRate.value.optionsCurrencyData,
  (newVal, oldVal) => {
    if (!isEqual(oldVal, newVal)) {
      updateStrikeRates();
    }
  },
  { immediate: true, deep: true }
);

function changePremiumCurrency(currency: string) {
  emit('update:solution', { ...props.solution, premiumCurrency: currency });
}

function changePremiumType() {
  emit('update:solution', { ...props.solution, premiumType: premiumType.value });
}

const premiumType = computed<PremiumPaymentType>(() =>
  displayMonthlyPaymentOption.value ? premiumPaymentForm.form.paymentType : PremiumPaymentType.UPFRONT
);

watch(premiumType, changePremiumType, { immediate: true });

const paymentOptionsUpfront = computed(() => {
  if (showProtectionInSell.value) {
    return sellCcy.value.optionsCurrencyData.paymentOptions.find(
      (paymentOption) => paymentOption.premiumPaymentType === PremiumPaymentType.UPFRONT
    );
  } else {
    return buyCcy.value.optionsCurrencyData.paymentOptions.find(
      (paymentOption) => paymentOption.premiumPaymentType === PremiumPaymentType.UPFRONT
    );
  }
});

const paymentOptionsMonthly = computed(() => {
  if (showProtectionInSell.value) {
    return sellCcy.value.optionsCurrencyData.paymentOptions.find(
      (paymentOption) => paymentOption.premiumPaymentType === PremiumPaymentType.MONTHLY
    );
  } else {
    return buyCcy.value.optionsCurrencyData.paymentOptions.find(
      (paymentOption) => paymentOption.premiumPaymentType === PremiumPaymentType.MONTHLY
    );
  }
});

const payNowAmount = computed(() => formatCurrencyValue(paymentOptionsUpfront.value?.totalPremium));

const payMonthlyAmount = computed(() =>
  formatCurrencyValue(paymentOptionsMonthly.value?.premiumPaymentSchedule[0].amount)
);

const payMonthlyTotalAmount = computed(() => formatCurrencyValue(paymentOptionsMonthly.value?.totalPremium));

const payPremiumCurrency = computed(() =>
  showProtectionInSell.value ? sellCurrency.value ?? '-' : buyCurrency.value ?? '-'
);

const numberOfMonthsText = computed(() => {
  const premiumPayments = paymentOptionsMonthly.value?.premiumPaymentSchedule;
  const numberOfPayments = premiumPayments?.length;

  return premiumPayments && numberOfPayments ? `${numberOfPayments} ${numberOfPayments > 1 ? 'months' : 'month'}` : '-';
});

function getDescription(paymentType: PremiumPaymentType) {
  return paymentType === PremiumPaymentType.UPFRONT
    ? `Pay ${payPremiumCurrency.value} ${payNowAmount.value} today`
    : `Pay ${payPremiumCurrency.value} ${payMonthlyTotalAmount.value} over the next ${numberOfMonthsText.value}`;
}

return { __sfc: true,emit, tradeState, props, state, onBehalfOfClient, displayMonthlyPaymentOption, strikeRateForm, settlementDateForm, options, premiumPaymentForm, loadNonTradeableDays, sellCcy, buyCcy, sellAmount, buyAmount, strikeRate, sellCurrency, buyCurrency, showProtectionInSell, isPrimaryRate, isFixedSide, setErrorMessage, isInvalid, updateValidations, onSettlementDateFormEvent, onFormEvent, displayRate, calcRatePosition, equalToRate, updateStrikeRates, updateSettlementDate, changePremiumCurrency, changePremiumType, premiumType, paymentOptionsUpfront, paymentOptionsMonthly, payNowAmount, payMonthlyAmount, payMonthlyTotalAmount, payPremiumCurrency, numberOfMonthsText, getDescription, formatDate, PremiumPaymentType, formatCurrencyValue }
}

})